import { Panel, PanelBody } from "@autonoma/blacklight";
import { GlobeIcon } from "@phosphor-icons/react/Globe";
import { createFileRoute, notFound } from "@tanstack/react-router";
import type { PreviewLogSource } from "components/build-logs/preview-logs-tabs";
import { ensureBranchByPrData } from "lib/query/branches.queries";
import {
  ensurePreviewEnvironmentSummaryData,
  usePreviewEnvironmentSummary,
  usePreviewSummaryById,
} from "lib/query/deployments.queries";
import { Suspense } from "react";
import { useCurrentApplication } from "routes/_blacklight/_app-shell/-use-current-application";
import {
  EnvironmentSummaryStrip,
  EnvironmentSummaryStripSkeleton,
} from "../../-components/preview/environment-summary-strip";
import {
  PreviewEnvironmentExplorer,
  PreviewEnvironmentExplorerSkeleton,
} from "../../-components/preview/preview-environment-explorer";

// Persisted in the URL so a refresh keeps the selected service and the chosen log focus (build vs app).
type PreviewSearch = { service?: string; logs?: PreviewLogSource };

export const Route = createFileRoute("/_blacklight/_app-shell/app/$appSlug/pull-requests/$prNumber/_tabs/preview")({
  loader: async ({ context, params: { appSlug, prNumber } }) => {
    const app = context.applications.find((a) => a.slug === appSlug);
    if (app == null) throw notFound();
    // Both are keyed by (applicationId, prNumber) only, so neither waits on the other - sequential awaits
    // here cost the tab a whole round trip of blank screen for nothing.
    await Promise.all([
      ensureBranchByPrData(context.queryClient, app.id, prNumber),
      ensurePreviewEnvironmentSummaryData(context.queryClient, app.id, prNumber),
    ]);
  },
  validateSearch: (search: Record<string, unknown>): PreviewSearch => ({
    service: typeof search.service === "string" ? search.service : undefined,
    logs: search.logs === "build" || search.logs === "app" ? search.logs : undefined,
  }),
  // The same skeleton the body suspends into, so the loader's wait and the render's wait look identical.
  pendingComponent: PreviewTabPending,
  component: PreviewTab,
});

/** The loader runs before `PreviewTab`'s padded wrapper exists, so the pending state supplies it. */
function PreviewTabPending() {
  return (
    <div className="flex min-h-0 flex-1 flex-col p-6">
      <PreviewPageSkeleton />
    </div>
  );
}

function PreviewTab() {
  const { prNumber } = Route.useParams();

  return (
    <div className="flex min-h-0 flex-1 flex-col p-6">
      <Suspense fallback={<PreviewPageSkeleton />}>
        <PreviewTabBody prNumber={prNumber} />
      </Suspense>
    </div>
  );
}

function PreviewTabBody({ prNumber }: { prNumber: number }) {
  const app = useCurrentApplication();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { data: envSummary } = usePreviewEnvironmentSummary(app.id, prNumber, { refetchWhileActive: true });

  if (envSummary.source !== "previewkit") return <NoPreviewPanel />;

  return (
    <PreviewByEnvironment
      applicationId={app.id}
      environmentId={envSummary.environmentId}
      search={search}
      onSearchChange={(partial) => void navigate({ search: (prev) => ({ ...prev, ...partial }), replace: true })}
    />
  );
}

function PreviewByEnvironment({
  applicationId,
  environmentId,
  search,
  onSearchChange,
}: {
  applicationId: string;
  environmentId: string;
  search: PreviewSearch;
  onSearchChange: (partial: PreviewSearch) => void;
}) {
  const { data: summary } = usePreviewSummaryById(applicationId, environmentId, { refetchWhileActive: true });

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <EnvironmentSummaryStrip applicationId={applicationId} environmentId={environmentId} summary={summary} />
      <PreviewEnvironmentExplorer
        applicationId={applicationId}
        environmentId={environmentId}
        summary={summary}
        search={search}
        onSearchChange={onSearchChange}
      />
    </div>
  );
}

function NoPreviewPanel() {
  return (
    <Panel>
      <PanelBody>
        <div className="flex flex-col items-center justify-center gap-3 py-14 text-center text-text-secondary">
          <GlobeIcon size={28} />
          <p className="text-sm">No preview environment for this pull request.</p>
        </div>
      </PanelBody>
    </Panel>
  );
}

// Mirrors PreviewByEnvironment's layout (summary strip + rail/center) so there's no layout shift
// once data loads.
function PreviewPageSkeleton() {
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <EnvironmentSummaryStripSkeleton />
      <PreviewEnvironmentExplorerSkeleton />
    </div>
  );
}
